import { defaultVariableDefinition } from 'pages/AutomationEngine/steps';
import {
  UiVariable,
  ApiVariable,
  ApiVariableSourcesValue,
  ApiLoggedActivityValue,
  ApiDeletedOption,
  ApiValueCategoryOption,
  LoggedActivityVariableSourceSubTypes,
  ApiDataSubtype,
  SourceVariableType,
  ApiVariableSource,
} from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import { DeletedOption } from 'pages/AutomationEngine/types';
import { GenericOption } from 'pages/SmartConnectors/types';
import { VariableDataType } from 'ts-components/inputs/ValueInput/types';
import {
  MessageDictionary,
  AddErrorMessage,
} from 'pages/AutomationEngine/messageDictionary';

const addDeleted = (label: string, deleted: boolean | undefined) =>
  deleted ? `${label} [Deleted]` : label;

const deletedOptionForFlow = (
  option: DeletedOption,
  addErrorMessage: AddErrorMessage,
  errorMessage: string
) => {
  if (option.deleted) {
    addErrorMessage(errorMessage);
  }

  return {
    ...option,
    label: addDeleted(option.label, option.deleted),
  };
};

const transformApiLoggedActivityValueToDeletedOption = ({
  id,
  description,
  displayName,
  deleted,
}: ApiLoggedActivityValue) => ({
  value: id,
  label: description || displayName,
  deleted,
});

// Static Value Translators

const transformApiValueCategoryToDeletedOption = ({
  id,
  objectName,
  description,
  deleted,
}: ApiValueCategoryOption) => ({
  value: id,
  label: description || objectName,
  deleted: Boolean(deleted),
});

const transformApiDataSubtype = ({
  id,
  name,
  description,
  deleted,
}: ApiDataSubtype): DeletedOption => ({
  label: description || name,
  value: id,
  deleted: !!deleted,
});

// Generic translate to DeletedOption

const GenericTranslateToDeletedOption = <Type>(
  value: any,
  transX: (value: Type) => DeletedOption,
  addErrorMessage: AddErrorMessage,
  errorMessage: string
) => {
  if (!value || typeof value === 'string') {
    return value;
  }

  return deletedOptionForFlow(transX(value), addErrorMessage, errorMessage);
};

const valueCategoryForApi = (
  valueCategory: GenericOption | string | undefined
): string | undefined => {
  if (!valueCategory || typeof valueCategory === 'string') {
    return valueCategory;
  }

  return valueCategory?.value;
};

export const mapApiSource = (source: SourceVariableType) => {
  switch (source.sourceType) {
    case 'static': {
      const { staticVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: source.sourceSubtype,
        value:
          typeof staticVariable?.value === 'object'
            ? staticVariable?.value?.value
            : staticVariable?.value,
        valueCategory: valueCategoryForApi(staticVariable?.valueCategory),
      };
    }
    case 'activity_past_due_trigger': {
      const { activityPassedDueVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: valueCategoryForApi(
          activityPassedDueVariable?.sourceSubtype
        ),
        value: activityPassedDueVariable?.value
          ? valueCategoryForApi(activityPassedDueVariable?.value)
          : null,

        valueCategory: valueCategoryForApi(
          activityPassedDueVariable?.valueCategory
        ),
      };
    }
    case 'logged_activity_trigger': {
      const { loggedActivityVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: valueCategoryForApi(
          loggedActivityVariable?.sourceSubtype
        ),
        value: loggedActivityVariable?.value
          ? valueCategoryForApi(loggedActivityVariable?.value)
          : null,

        valueCategory: valueCategoryForApi(
          loggedActivityVariable?.valueCategory
        ),
      };
    }
    case 'field_value': {
      const { fieldVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: valueCategoryForApi(fieldVariable?.sourceSubtype),
        field: valueCategoryForApi(fieldVariable?.field),
        relationshipField: valueCategoryForApi(
          fieldVariable?.relationshipField
        ),
      };
    }
    case 'variable': {
      return {
        sourceType: source.sourceType,
        variable:
          typeof source.automationVariable?.value === 'object'
            ? source.automationVariable?.value?.label
            : source.automationVariable?.value,
      };
    }
    default:
      return {};
  }
};

export const variableForApi = (
  currentVariables: any[],
  uiVariable: UiVariable,
  order: number
) => {
  if (uiVariable.type === 'initialize_variable') {
    const result = { ...uiVariable, order };
    const { details: orgDetails, id, considerEmptyAnError, ...rest } = result;

    const sources = (orgDetails.sources || []).map(mapApiSource);

    const dataSubtype =
      orgDetails.variable.dataSubtype &&
      typeof orgDetails.variable.dataSubtype === 'object'
        ? orgDetails.variable?.dataSubtype.value
        : orgDetails.variable?.dataSubtype;

    const details = {
      isRequired: considerEmptyAnError,
      variable: { ...orgDetails.variable, dataSubtype },
      sources,
    };

    currentVariables.push({
      // we need id especially to update action and if it's not a new trigger
      ...(!id.startsWith('new') && { id }),
      [`action_initialize_variable`]: details,
      key: id,
      ...rest,
    });
  }
  return currentVariables;
};

const staticVariableValue = (
  staticValue: ApiVariableSourcesValue,
  addErrorMessage: AddErrorMessage,
  sourceSubtype: VariableDataType,
  messageDictionary: MessageDictionary
) => {
  if (!staticValue || typeof staticValue === 'string') {
    return staticValue;
  }

  if (staticValue.deleted) {
    switch (sourceSubtype) {
      case 'entity':
        addErrorMessage(messageDictionary.objectRecordHasBeenDeleted);
        break;

      case 'employee':
        addErrorMessage(messageDictionary.teamMemberHasBeenDeleted);
        break;

      default:
        addErrorMessage(messageDictionary.fieldDeleted);
        break;
    }
  }

  return {
    value: staticValue.id,
    label: addDeleted(staticValue.displayName, staticValue.deleted),
    deleted: staticValue.deleted,
  };
};

const optionForUI = (
  option: ApiDeletedOption | string | undefined,
  addErrorMessage: AddErrorMessage,
  errorMessage: string
) => {
  if (!option || typeof option === 'string') {
    return option;
  }

  if (option.deleted) {
    addErrorMessage(errorMessage);
  }

  return {
    value: option.id,
    label: addDeleted(option.name, option.deleted),
    deleted: option.deleted,
  };
};

export const mapFlowSource =
  (
    variableId: string,
    messageDictionary: MessageDictionary,
    addErrorMessage: any
  ) =>
  (source: ApiVariableSource, index: number) => {
    const {
      sourceType,
      sourceSubtype,
      value,
      valueCategory,
      field,
      relationshipField,
    } = source;

    const sourceId = `${variableId}-${index}`;
    switch (sourceType) {
      case 'static':
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          staticVariable: {
            valueCategory:
              GenericTranslateToDeletedOption<ApiValueCategoryOption>(
                valueCategory,
                transformApiValueCategoryToDeletedOption,
                addErrorMessage,
                sourceSubtype === 'entity'
                  ? messageDictionary.objectHasBeenDeleted
                  : messageDictionary.fieldDeleted
              ),
            value: staticVariableValue(
              value,
              addErrorMessage,
              sourceSubtype,
              messageDictionary
            ),
            sourceSubtype,

            variableType: sourceType,
            isValid: true,
          },
        };

      case 'logged_activity_trigger':
      case 'activity_past_due_trigger':
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          [sourceType === 'logged_activity_trigger'
            ? 'loggedActivityVariable'
            : 'activityPassedDueVariable']: {
            sourceSubtype,
            valueCategory: optionForUI(
              valueCategory as unknown as ApiDeletedOption,
              addErrorMessage,
              messageDictionary.activityDeleted
            ),
            value: GenericTranslateToDeletedOption<ApiLoggedActivityValue>(
              value,
              transformApiLoggedActivityValueToDeletedOption,
              addErrorMessage,
              (sourceSubtype as LoggedActivityVariableSourceSubTypes) ===
                'record_association'
                ? messageDictionary.activityFieldValueDeleted
                : messageDictionary.associatedObjectDeleted
            ),
            variableType: sourceType,
            isValid: true,
          },
        };

      case 'field_value':
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          fieldVariable: {
            sourceSubtype,
            variableType: sourceType,
            field: GenericTranslateToDeletedOption<ApiLoggedActivityValue>(
              field,
              transformApiLoggedActivityValueToDeletedOption,
              addErrorMessage,
              messageDictionary.fieldDeleted
            ),
            relationshipField:
              GenericTranslateToDeletedOption<ApiLoggedActivityValue>(
                relationshipField,
                transformApiLoggedActivityValueToDeletedOption,
                addErrorMessage,
                messageDictionary.fieldDeleted
              ),
            isValid: true,
          },
        };

      case 'variable': {
        const { variable } = source;

        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          automationVariable: {
            ...variable,
            isValid: true,
            value: {
              label: variable.name,
              value: variable.name,
              meta: {
                dataType: sourceType,
                dataSubtype: sourceSubtype,
                sourceType,
              },
            },
          },
        };
      }
      default:
        return source;
    }
  };

export const variablesForFlow = (
  apiVariables: ApiVariable[],
  messageDictionary: MessageDictionary
) => {
  const variablesWithErrors = apiVariables.map((variable, order) => {
    const variableErrors: string[] = [];
    const addErrorMessage = (message: string) => {
      variableErrors.push(message);
    };

    const { actionInitializeVariable, ...rest } = variable;
    const sources = actionInitializeVariable.sources.map(
      mapFlowSource(variable?.id, messageDictionary, addErrorMessage)
    );
    if (!actionInitializeVariable?.variable) {
      addErrorMessage(messageDictionary.automationFailedToLoad);
    }
    const dataSubtype = GenericTranslateToDeletedOption<ApiDataSubtype>(
      actionInitializeVariable?.variable?.dataSubtype,
      transformApiDataSubtype,
      addErrorMessage,
      messageDictionary.objectHasBeenDeleted
    );
    return {
      variable: {
        ...rest,
        type: variable.stepType,
        order,
        details: {
          ...actionInitializeVariable,
          variable: { ...actionInitializeVariable.variable, dataSubtype },
          sources,
        },
        hasError: variableErrors.length ? variableErrors : false,
        considerEmptyAnError: actionInitializeVariable.isRequired,
      },
      error: variableErrors.length
        ? { id: variable.id, errors: variableErrors }
        : null,
    };
  });

  return {
    variables: [
      ...variablesWithErrors.map(({ variable }) => variable),
      { ...defaultVariableDefinition, order: variablesWithErrors.length },
    ],
    variableErrors: variablesWithErrors
      .filter(({ error }) => !!error)
      .map(({ error }) => error),
  };
};
